import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChatBubble } from "@/components/ChatBubble";
import { Button } from "@/components/ui/button";

import { User, RiskLevel, AIExtraction, RecommendedAction, UserRole } from "@/types/sorin";
import { Mic, Camera, Send, Loader2, Menu, X, Upload, Plus, Shield, BookOpen, ClipboardCheck, CheckCircle, AlertTriangle, Sparkles, FileText, Lock, RefreshCw, MessageCircle, Square } from "lucide-react";
import { cn } from "@/lib/utils";
import { NotificationBell } from "@/components/NotificationBell";

import { ClarificationPopup } from "@/components/ClarificationPopup";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { AudioRecorder, blobToBase64 } from "@/utils/audioRecorder";
import { QuestGiftBox } from "@/components/QuestGiftBox";
import InterventionPopup from "@/components/InterventionPopup";
import { CorroborationPopup } from "@/components/CorroborationPopup";

import { DailyMessageBanner } from "@/components/DailyMessageBanner";
import { GeminiMenu } from "@/components/GeminiMenu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { motion, AnimatePresence } from "framer-motion";
import { useSubscriptionFeatures } from "@/hooks/useSubscriptionFeatures";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { isNativePlatform, capturePhoto, photoToFile } from "@/utils/nativeCamera";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { useOfflineStatus } from "@/hooks/useOfflineStatus";
import { OfflineHazardForm } from "@/components/OfflineHazardForm";
import { OfflineIncidentForm } from "@/components/OfflineIncidentForm";
import { useCachedCoworkers, useCachedCheckinLocation } from "@/hooks/useCachedData";
import { savePendingReport, generateOfflineId } from "@/utils/offlineStorage";

// Subscription-gated input bar component
const SubscriptionGatedInputBar = ({
  fileInputRef,
  inputText,
  setInputText,
  handleSendMessage,
  handleCameraClick,
  handleMicClick,
  handlePhotoSelect,
  isLoading,
  isAnalyzingPhoto,
  isRecording,
  onStop,
  aboveInputContent,
  companyId,
}: {
  fileInputRef: React.RefObject<HTMLInputElement>;
  inputText: string;
  setInputText: (text: string) => void;
  handleSendMessage: () => void;
  handleCameraClick: () => void;
  handleMicClick: () => void;
  handlePhotoSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isLoading: boolean;
  isAnalyzingPhoto: boolean;
  isRecording: boolean;
  onStop?: () => void;
  aboveInputContent?: React.ReactNode;
  companyId?: string | null;
}) => {
  const { hasFeature } = useSubscriptionFeatures(companyId || undefined);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const canUsePhoto = hasFeature('hazardReportingPhoto');
  const canUseVoice = hasFeature('hazardReportingVoice');

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [inputText]);

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handlePhotoSelect}
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          opacity: 0,
          pointerEvents: 'none',
        }}
      />

      {/* Above-input content (cards, chips, photo preview) */}
      {aboveInputContent}

      <div className="max-w-2xl mx-auto space-y-2">
        {/* Textarea - full width */}
        <textarea
          ref={textareaRef}
          placeholder="Ketik pesan..."
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage();
            }
          }}
          rows={1}
          className="flex w-full resize-none rounded-2xl px-4 py-3 bg-secondary border-0 text-base placeholder:text-muted-foreground focus:outline-none focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 max-h-[120px] overflow-y-auto"
          disabled={isLoading}
        />

        {/* Action buttons row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            {/* Camera Button */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={canUsePhoto ? handleCameraClick : undefined}
                    disabled={isLoading || isAnalyzingPhoto || !canUsePhoto}
                    className={`h-10 w-10 rounded-full shrink-0 ${!canUsePhoto ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isAnalyzingPhoto ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        <Camera className="h-5 w-5" />
                        {!canUsePhoto && <Lock className="h-3 w-3 absolute -top-0.5 -right-0.5 text-muted-foreground" />}
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                {!canUsePhoto && (
                  <TooltipContent side="top">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-primary" />
                      <span>Upgrade ke Professional untuk foto</span>
                    </div>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>

            {/* Mic Button */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={canUseVoice ? handleMicClick : undefined}
                    disabled={isLoading || !canUseVoice}
                    className={`h-10 w-10 rounded-full shrink-0 ${isRecording ? 'bg-destructive text-destructive-foreground animate-pulse' : ''} ${!canUseVoice ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Mic className="h-5 w-5" />
                    {!canUseVoice && <Lock className="h-3 w-3 absolute -top-0.5 -right-0.5 text-muted-foreground" />}
                  </Button>
                </TooltipTrigger>
                {!canUseVoice && (
                  <TooltipContent side="top">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-primary" />
                      <span>Upgrade ke Professional untuk voice</span>
                    </div>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Send/Stop button */}
          <Button
            size="icon"
            onClick={isLoading && onStop ? onStop : handleSendMessage}
            disabled={!isLoading && !inputText.trim()}
            className={cn(
              "h-10 w-10 rounded-full",
              isLoading ? "bg-destructive hover:bg-destructive/90" : "bg-primary hover:bg-primary/90"
            )}
          >
            {isLoading ? <Square className="h-4 w-4 fill-current" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </>
  );
};

// Module-level vars for long-press (survive React re-renders)
let _longPressTimer: ReturnType<typeof setTimeout> | null = null;
let _didLongPress = false;

const Home = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const profileRef = useRef<any>(null);
  const [user, setUser] = useState<User | null>(null);
  const [authUserId, setAuthUserId] = useState<string | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const [messages, setMessages] = useState<Array<{
    role: "user" | "assistant";
    content: string;
    timestamp: Date;
    imageUrl?: string;
  }>>([]);
  const [inputText, setInputText] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastExtraction, setLastExtraction] = useState<AIExtraction | null>(null);
  const [accumulatedExtraction, setAccumulatedExtraction] = useState<AIExtraction | null>(null);
  const [probeQuestions, setProbeQuestions] = useState<string[]>([]);
  const [actionSuggestions, setActionSuggestions] = useState<RecommendedAction[]>([]);
  const [currentRiskScore, setCurrentRiskScore] = useState<number | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isSavingReport, setIsSavingReport] = useState(false);
  const [isAnalyzingPhoto, setIsAnalyzingPhoto] = useState(false);
  const [photoAnalysisData, setPhotoAnalysisData] = useState<any>(null);
  const [audioConfidence, setAudioConfidence] = useState<number>(0);
  const [audioInputUsed, setAudioInputUsed] = useState<boolean>(false);
  const [chatMode, setChatMode] = useState<'hazard' | 'knowledge' | 'inspection' | null>(null);
  const [suggestedActions, setSuggestedActions] = useState<string[]>([]);
  const [showResolutionOptions, setShowResolutionOptions] = useState(false);
  const [isCreatingResolution, setIsCreatingResolution] = useState(false);
  const [proofPhotoFile, setProofPhotoFile] = useState<File | null>(null);
  const [proofPhotoPreview, setProofPhotoPreview] = useState<string | null>(null);
  const [showProofUpload, setShowProofUpload] = useState(false);
  const [inspectionId, setInspectionId] = useState<string | null>(null);
  const [inspectionChecklistId, setInspectionChecklistId] = useState<string | null>(null);
  const [inspectionStarted, setInspectionStarted] = useState(false);
  const [inspectionCompleted, setInspectionCompleted] = useState(false);
  const [isCompletingInspection, setIsCompletingInspection] = useState(false);
  const [pendingPhotoForFinding, setPendingPhotoForFinding] = useState(false);
  const [lastFindingSeverity, setLastFindingSeverity] = useState<string | null>(null);
  const [pendingCorroboration, setPendingCorroboration] = useState<{ reportId: string; notificationId: string } | null>(null);
  const [showCorroborationPopup, setShowCorroborationPopup] = useState(false);
  const [showOfflineForm, setShowOfflineForm] = useState(false);
  const [showOfflineIncidentForm, setShowOfflineIncidentForm] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  // Independent session state for each mode
  const [savedHazardSession, setSavedHazardSession] = useState<{
    chatMode: 'hazard';
    messages: Array<{ role: string; content: string; timestamp: string; imageUrl?: string }>;
    accumulatedExtraction: AIExtraction | null;
    savedAt: string;
  } | null>(null);
  const [savedInspectionSession, setSavedInspectionSession] = useState<{
    chatMode: 'inspection';
    messages: Array<{ role: string; content: string; timestamp: string; imageUrl?: string }>;
    accumulatedExtraction: AIExtraction | null;
    inspectionId: string | null;
    inspectionChecklistId: string | null;
    inspectionStarted: boolean;
    savedAt: string;
  } | null>(null);
  const [savedKnowledgeSession, setSavedKnowledgeSession] = useState<{
    chatMode: 'knowledge';
    messages: Array<{ role: string; content: string; timestamp: string; imageUrl?: string }>;
    savedAt: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const proofInputRef = useRef<HTMLInputElement>(null);
  const audioRecorderRef = useRef<AudioRecorder | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  // Offline status
  const { isOffline } = useOfflineStatus();

  // Cached data for offline use
  const { data: cachedCoworkers } = useCachedCoworkers(
    profile?.company_id || null,
    profile?.id || null,
    profile?.site_id
  );
  const { data: cachedCheckin } = useCachedCheckinLocation(profile?.id || null);

  // Push notification handler for notification tap navigation
  const handlePushNotificationTap = useCallback((data: Record<string, string>) => {
    if (data?.type === 'corroboration_request' && data?.report_id) {
      navigate(`/corroborate/${data.report_id}`);
    } else if (data?.type === 'quest_assigned') {
      navigate('/quests');
    } else if (data?.type === 'team_alert') {
      // Stay on home, refresh notifications
      toast({ title: "Peringatan Keselamatan", description: "Ada kondisi berbahaya di area kerja Anda." });
    }
  }, [navigate, toast]);

  // Initialize push notifications for native mobile app
  const { permissionStatus: pushPermission } = usePushNotifications(
    profile?.id || null,
    handlePushNotificationTap
  );

  useEffect(() => {
    const savedExtraction = sessionStorage.getItem("accumulatedExtraction");
    if (savedExtraction) {
      try {
        setAccumulatedExtraction(JSON.parse(savedExtraction));
      } catch (e) {
        console.error("Failed to load saved extraction:", e);
      }
    }

    // Restore saved chat sessions for each mode independently
    const modes = ['hazard', 'inspection', 'knowledge'] as const;
    modes.forEach(mode => {
      const storageKey = `sorinChatState_${mode}`;
      const savedState = sessionStorage.getItem(storageKey);
      if (savedState) {
        try {
          const parsed = JSON.parse(savedState);
          // Check if session is expired (24 hours)
          const savedAt = new Date(parsed.savedAt);
          const hoursSinceSave = (Date.now() - savedAt.getTime()) / (1000 * 60 * 60);
          if (hoursSinceSave > 24) {
            sessionStorage.removeItem(storageKey);
          } else {
            if (mode === 'hazard') setSavedHazardSession(parsed);
            else if (mode === 'inspection') setSavedInspectionSession(parsed);
            else if (mode === 'knowledge') setSavedKnowledgeSession(parsed);
          }
        } catch (e) {
          console.error(`Failed to restore ${mode} session:`, e);
          sessionStorage.removeItem(storageKey);
        }
      }
    });
  }, []);

  useEffect(() => {
    checkAuthAndLoadProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const checkAuthAndLoadProfile = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/signup");
      return;
    }
    const { data: profileData } = await supabase.from("worker_profiles").select("*").eq("user_id", session.user.id).maybeSingle();
    if (!profileData) {
      navigate("/signup");
      return;
    }

    const psychProfile = profileData.psychometric_profile;
    const isProfileComplete = psychProfile && typeof psychProfile === 'object' && Object.keys(psychProfile).length > 0;
    if (!isProfileComplete) {
      toast({ title: "Profil Belum Lengkap", description: "Silakan selesaikan asesmen psikometrik terlebih dahulu." });
      navigate("/onboarding");
      return;
    }

    const today = new Date().toISOString().split('T')[0];
    const { data: checkinData } = await supabase.from('shift_checkins').select('id').eq('worker_id', profileData.id).eq('check_in_date', today).maybeSingle();
    if (!checkinData) {
      toast({ title: "Check-in Diperlukan", description: "Silakan check-in shift terlebih dahulu." });
      navigate("/pre-shift-checkin");
      return;
    }

    setAuthUserId(session.user.id);
    setProfile(profileData);
    profileRef.current = profileData;
    let riskLevel: RiskLevel = "Rendah";
    if (profileData.current_risk_score >= 75) riskLevel = "Tinggi";
    else if (profileData.current_risk_score >= 50) riskLevel = "Sedang";

    setUser({
      id: profileData.id,
      displayName: profileData.display_name,
      role: profileData.role as UserRole,
      team: profileData.team,
      xp: profileData.total_xp || 0,
      streak: profileData.consecutive_safe_days || 0,
      badges: (profileData.badges || []) as any[],
      psychometricVector: [],
      baselineRisk: profileData.baseline_risk_score,
      currentRisk: profileData.current_risk_score,
      anonymousMode: false,
      createdAt: new Date(profileData.created_at)
    });

    // Check for pending corroboration requests and auto-popup
    checkPendingCorroboration(profileData.id);
  };

  const checkPendingCorroboration = async (workerId: string) => {
    try {
      const { data: pendingNotif } = await supabase
        .from('notifications')
        .select('id, data')
        .eq('recipient_id', workerId)
        .eq('notification_type', 'corroboration_request')
        .eq('is_read', false)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (pendingNotif?.data) {
        const notifData = pendingNotif.data as Record<string, any>;
        if (notifData.report_id) {
          setPendingCorroboration({
            reportId: notifData.report_id,
            notificationId: pendingNotif.id
          });
          setShowCorroborationPopup(true);
        }
      }
    } catch (error) {
      console.error('Error checking pending corroboration:', error);
    }
  };

  // Core AI send logic - used by both handleSendMessage and retry
  const sendHazardMessage = async (allMessages: Array<{ role: string; content: string }>, controller: AbortController) => {
    const { data, error } = await supabase.functions.invoke("sorin-chat-lovable", {
      body: { messages: allMessages.map(m => ({ role: m.role, content: m.content })), userId: authUserId }
    });
    if (error) throw error;
    if (controller.signal.aborted) return; // Cancelled by user

    const hasExtraction = !!data.toolResults?.extraction;
    const messageContent = data.message?.trim();

    if (messageContent) {
      setMessages(prev => [...prev, { role: "assistant" as const, content: messageContent, timestamp: new Date() }]);
    } else if (!hasExtraction) {
      setMessages(prev => [...prev, { role: "assistant" as const, content: t("chat.errorUnderstand"), timestamp: new Date() }]);
    }

    if (data.toolResults?.extraction) {
      const extraction = data.toolResults.extraction;
      const mergedExtraction = {
        ...accumulatedExtraction,
        ...extraction,
        location: extraction.location || accumulatedExtraction?.location,
        hazard_classification: extraction.hazard_classification || accumulatedExtraction?.hazard_classification,
        hazard_category: extraction.hazard_category || accumulatedExtraction?.hazard_category,
        description: extraction.description || accumulatedExtraction?.description,
        severity: extraction.severity || accumulatedExtraction?.severity,
        probability: extraction.probability || accumulatedExtraction?.probability,
        reported_worker_id: extraction.reported_worker_id || accumulatedExtraction?.reported_worker_id,
        reported_person: extraction.reported_person || accumulatedExtraction?.reported_person,
        risk_matrix: extraction.risk_matrix || accumulatedExtraction?.risk_matrix,
        work_activity: extraction.work_activity || accumulatedExtraction?.work_activity,
        cause_category: extraction.cause_category || accumulatedExtraction?.cause_category,
        cause_details: extraction.cause_details || accumulatedExtraction?.cause_details,
      };
      setLastExtraction(extraction);
      setAccumulatedExtraction(mergedExtraction);
      sessionStorage.setItem("accumulatedExtraction", JSON.stringify(mergedExtraction));
      if (data.toolResults.probeQuestions) setProbeQuestions(data.toolResults.probeQuestions);
      if (extraction.recommended_actions) setActionSuggestions(extraction.recommended_actions);
      if (data.toolResults.corrective_actions?.length > 0) setSuggestedActions(data.toolResults.corrective_actions);
      if (extraction.risk_matrix?.risk_score) setCurrentRiskScore(extraction.risk_matrix.risk_score);
    }
  };

  const handleSendMessage = async () => {
    if (!inputText.trim() || isLoading) return;
    if (chatMode === 'knowledge') { await handleKnowledgeQuery(inputText); return; }
    if (chatMode === 'inspection') { await handleInspectionQuery(inputText); return; }

    const userMsg = { role: "user" as const, content: inputText, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInputText("");
    setIsLoading(true);
    const controller = new AbortController();
    setAbortController(controller);
    try {
      await sendHazardMessage([...messages, userMsg], controller);
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error("Error calling AI:", error);
      setMessages(prev => [...prev, { role: "assistant" as const, content: t("chat.errorGeneral"), timestamp: new Date() }]);
    } finally {
      setIsLoading(false);
      setAbortController(null);
    }
  };

  // Retry: directly re-send with content, bypassing inputText state
  const retryWithContent = async (content: string, mode: 'hazard' | 'knowledge' | 'inspection') => {
    if (isLoading) return;
    setIsLoading(true);
    const controller = new AbortController();
    setAbortController(controller);
    try {
      if (mode === 'knowledge') {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        const { data, error } = await supabase.functions.invoke("sorin-knowledge-query", { body: { query: content, hazardCategory: accumulatedExtraction?.hazard_category || null, userId: authUser?.id || null } });
        if (error) throw error;
        if (controller.signal.aborted) return;
        let responseContent = data.answer || "Maaf, tidak ditemukan informasi yang relevan.";
        if (data.sources?.length > 0) responseContent += `\n\n📚 Referensi: ${data.sources.join(', ')}`;
        if (data.corrective_actions?.length > 0) {
          setSuggestedActions(data.corrective_actions);
          responseContent += "\n\n✅ Tindakan yang disarankan:";
          data.corrective_actions.forEach((action: string, idx: number) => { responseContent += `\n${idx + 1}. ${action}`; });
        }
        setMessages(prev => [...prev, { role: "assistant" as const, content: responseContent, timestamp: new Date() }]);
      } else if (mode === 'inspection') {
        const inspectionContext = inspectionId ? { inspectionId, checklistId: inspectionChecklistId, pendingPhoto: pendingPhotoForFinding } : null;
        const { data, error } = await supabase.functions.invoke("sorin-inspection-chat", { body: { message: content, userId: authUserId, siteId: profile?.site_id || null, inspectionContext, conversationHistory: messages.map(m => ({ role: m.role, content: m.content })) } });
        if (error) throw error;
        if (controller.signal.aborted) return;
        const responseContent = data.message || "Maaf, ada masalah. Coba lagi.";
        setMessages(prev => [...prev, { role: "assistant" as const, content: responseContent, timestamp: new Date() }]);
      } else {
        // Hazard mode - messages already contain the user msg
        await sendHazardMessage(messages.map(m => ({ role: m.role, content: m.content })), controller);
      }
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error("Retry error:", error);
      setMessages(prev => [...prev, { role: "assistant" as const, content: "Maaf, ada masalah. Coba lagi nanti.", timestamp: new Date() }]);
    } finally {
      setIsLoading(false);
      setAbortController(null);
    }
  };

  const submitReportFromExtraction = async (): Promise<string | null> => {
    if (!accumulatedExtraction || isSavingReport) return null;

    // Only description is truly required - allow partial submissions
    if (!accumulatedExtraction.description) {
      toast({ title: "Error", description: "Deskripsi bahaya diperlukan. Ceritakan apa yang terjadi.", variant: "destructive" });
      return null;
    }

    // Show warning for partial submissions
    const isPartialSubmission = !accumulatedExtraction.hazard_category || !accumulatedExtraction.hazard_classification;
    if (isPartialSubmission) {
      toast({
        title: "Informasi Terbatas",
        description: "Laporan akan disimpan dengan skor kualitas lebih rendah karena informasi belum lengkap.",
        variant: "default"
      });
    }
    setIsSavingReport(true);
    let createdReportId: string | null = null;

    try {
      if (!user || !profileRef.current) {
        toast({ title: "Error", description: "Profile not found. Please refresh the page.", variant: "destructive" });
        return null;
      }

      // Capture photo ref before reset
      const capturedPhotoFile = photoFile;
      const capturedPhotoAnalysisData = photoAnalysisData;

      const qualityScore = Math.round((accumulatedExtraction.description ? 30 : 0) + (accumulatedExtraction.location ? 20 : 0) + (accumulatedExtraction.severity ? 25 : 0) + (accumulatedExtraction.probability ? 25 : 0));

      // Default missing fields for partial submissions
      const finalHazardCategory = accumulatedExtraction.hazard_category || "other";
      const finalHazardClassification = accumulatedExtraction.hazard_classification || "behavioral";

      // Insert report immediately WITHOUT waiting for photo upload (optimistic)
      const { data: insertedReport, error: dbError } = await supabase.from("peer_reports").insert({
        description: accumulatedExtraction.description,
        location: accumulatedExtraction.location || "Lokasi tidak ditentukan",
        hazard_classification: finalHazardClassification,
        hazard_category: finalHazardCategory,
        severity: accumulatedExtraction.severity || 2,
        probability: accumulatedExtraction.probability || 2,
        risk_score: accumulatedExtraction.risk_matrix?.risk_score || 4,
        risk_rating: accumulatedExtraction.risk_matrix?.risk_rating || "low",
        verification_status: "pending_verification",
        reporter_id: profileRef.current.id,
        reported_worker_id: accumulatedExtraction.reported_worker_id || null,
        quality_score: qualityScore,
        photo_url: null, // Will be updated in background if photo exists
        photo_analysis_data: capturedPhotoAnalysisData,
        audio_input_method: audioInputUsed ? "voice" : null,
        audio_confidence: audioConfidence,
        xp_pending: true,
        xp_awarded: 0,
        site_id: profileRef.current.site_id || null,
        work_activity: accumulatedExtraction.work_activity || null,
        cause_category: accumulatedExtraction.cause_category || null,
        cause_details: accumulatedExtraction.cause_details || null,
      }).select().single();

      if (dbError) throw dbError;

      createdReportId = insertedReport?.id ?? null;

      // === IMMEDIATE SUCCESS FEEDBACK ===
      toast({ title: t("chat.reportSaved"), description: "Laporan berhasil disimpan!" });

      // Reset UI immediately - user can start new report
      setAccumulatedExtraction(null);
      setPhotoFile(null);
      setPhotoPreview(null);
      setPhotoAnalysisData(null);
      setAudioConfidence(0);
      setAudioInputUsed(false);
      setProbeQuestions([]);
      setActionSuggestions([]);
      setCurrentRiskScore(null);
      setSuggestedActions([]);
      setShowProofUpload(false);
      setProofPhotoFile(null);
      setProofPhotoPreview(null);
      setChatMode(null);
      setMessages([]);
      sessionStorage.removeItem("accumulatedExtraction");
      sessionStorage.removeItem("sorinChatState_hazard");
      setSavedHazardSession(null);

      // === FIRE-AND-FORGET: Background processing ===
      if (insertedReport) {
        const reportId = insertedReport.id;
        const reportedWorkerId = accumulatedExtraction.reported_worker_id;
        const riskRating = insertedReport.risk_rating;
        const hazardClassification = insertedReport.hazard_classification;
        const currentProfileId = profileRef.current.id;

        // Background photo upload + update report row
        if (capturedPhotoFile) {
          const file = capturedPhotoFile;
          const pid = currentProfileId;
          setTimeout(async () => {
            try {
              const fileName = `${pid}/${Date.now()}_${file.name}`;
              const { error: uploadError } = await supabase.storage.from("hazard-photos").upload(fileName, file);
              if (!uploadError) {
                const { data: { publicUrl } } = supabase.storage.from("hazard-photos").getPublicUrl(fileName);
                await supabase.from("peer_reports").update({ photo_url: publicUrl }).eq('id', reportId);
              }
            } catch (e) {
              console.error('Background photo upload failed:', e);
            }
          }, 0);
        }

        Promise.all([
          supabase.functions.invoke("calculate-evidence-score", { body: { reportId } }),
          supabase.functions.invoke("track-reporting-pattern", { body: { reporterId: currentProfileId, reportedWorkerId } }),
          (hazardClassification === "unsafe_condition" && (riskRating === "high" || riskRating === "very_high"))
            ? supabase.functions.invoke("send-alert", { body: { reportId, alertType: "team_alert" } })
            : Promise.resolve(null),
          reportedWorkerId
            ? supabase.functions.invoke("predict-risk-score", { body: { workerId: reportedWorkerId } })
            : Promise.resolve(null),
        ]).catch(err => {
          console.error('Background processing error:', err);
        });
      }

    } catch (error) {
      console.error("Error saving report:", error);
      toast({ title: "Error", description: "Failed to save report. Please try again.", variant: "destructive" });
    } finally {
      setIsSavingReport(false);
    }
    return createdReportId;
  };

  const handleMicClick = async () => {
    if (isRecording) {
      try {
        if (!audioRecorderRef.current) return;
        const audioBlob = await audioRecorderRef.current.stop();
        setIsRecording(false);
        toast({ title: t("chat.recordingStopped"), description: t("chat.processingAudio") });
        const base64Audio = await blobToBase64(audioBlob);
        const { data, error } = await supabase.functions.invoke("transcribe-audio", { body: { audio: base64Audio } });
        if (error) throw error;
        if (data?.text) {
          setInputText(data.text);
          setAudioConfidence(data.audio_confidence || 0.7);
          setAudioInputUsed(true);
          toast({ title: "Transcription complete", description: "You can edit the text before sending" });
        }
      } catch (error) {
        console.error("Transcription error:", error);
        toast({ title: "Transcription failed", description: "Please try again or type your message", variant: "destructive" });
        setIsRecording(false);
      }
    } else {
      try {
        audioRecorderRef.current = new AudioRecorder();
        await audioRecorderRef.current.start();
        setIsRecording(true);
        toast({ title: t("chat.recordingStarted"), description: t("chat.speakNow") });
      } catch (error) {
        console.error("Recording error:", error);
        toast({ title: "Recording failed", description: "Please check microphone permissions", variant: "destructive" });
      }
    }
  };

  const handleCameraClick = async () => {
    // Check if on native platform and use native camera
    if (isNativePlatform()) {
      try {
        const photo = await capturePhoto('prompt');
        if (!photo) return; // User cancelled

        const file = await photoToFile(photo);
        if (!file) {
          toast({ title: "Gagal", description: "Tidak dapat memproses foto", variant: "destructive" });
          return;
        }

        // Process the file same as web upload
        await processPhotoFile(file, photo.webPath);
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage === 'PERMISSION_DENIED') {
          toast({
            title: "Izin Diperlukan",
            description: "Silakan izinkan akses kamera/galeri di pengaturan perangkat Anda",
            variant: "destructive"
          });
        } else {
          console.error('Camera capture error:', error);
          toast({ title: "Gagal mengambil foto", description: "Coba lagi", variant: "destructive" });
        }
      }
    } else {
      fileInputRef.current?.click();
    }
  };

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processPhotoFile(file);
  };

  const processPhotoFile = async (file: File, previewUrl?: string) => {
    if (chatMode === 'knowledge') {
      toast({ title: "Mode Cek Cara Kerja", description: "Untuk mode ini, cukup ketik pertanyaan Anda tentang prosedur kerja." });
      return;
    }
    if (chatMode === 'inspection') { await handleInspectionPhoto(file); return; }

    setPhotoFile(file);

    // If we have a preview URL from native camera, use it; otherwise read from file
    if (previewUrl) {
      setPhotoPreview(previewUrl);
      await analyzeAndUploadPhoto(file, previewUrl);
    } else {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const preview = reader.result as string;
        setPhotoPreview(preview);
        await analyzeAndUploadPhoto(file, preview);
      };
      reader.readAsDataURL(file);
    }
  };

  const analyzeAndUploadPhoto = async (file: File, _preview: string) => {
    setIsAnalyzingPhoto(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const fileExt = file.name.split(".").pop() || 'jpg';
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;
      const { data, error } = await supabase.storage.from("hazard-photos").upload(fileName, file);
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from("hazard-photos").getPublicUrl(data.path);
      const { data: analysisData, error: analysisError } = await supabase.functions.invoke("analyze-photo", { body: { imageUrl: publicUrl } });
      if (analysisError) throw analysisError;
      if (analysisData?.analysis) {
        const analysis = analysisData.analysis;
        setPhotoAnalysisData(analysis);
        const userPhotoMsg = { role: "user" as const, content: "Saya upload foto bahaya ini", timestamp: new Date(), imageUrl: publicUrl };
        setMessages(prev => [...prev, userPhotoMsg]);
        const analyzingMsg = { role: "assistant" as const, content: "Sorin sedang menganalisis foto...", timestamp: new Date() };
        setMessages(prev => [...prev, analyzingMsg]);
        const photoContext = `[PHOTO UPLOADED] The AI detected: ${analysis.summary}. Hazard type: ${analysis.hazard_type}, severity: ${analysis.severity}/5. Confidence: ${analysis.confidence}.`;
        try {
          const { data: chatData, error: chatError } = await supabase.functions.invoke("sorin-chat-lovable", { body: { messages: [...messages.map(m => ({ role: m.role, content: m.content })), { role: "user", content: photoContext }], userId: authUserId } });
          if (chatError) throw chatError;
          if (chatData?.message) {
            setMessages(prev => prev.slice(0, -1));
            const aiResponse = { role: "assistant" as const, content: chatData.message, timestamp: new Date() };
            setMessages(prev => [...prev, aiResponse]);
            if (chatData.toolResults?.extraction) {
              const extraction = chatData.toolResults.extraction;
              const mergedExtraction = { ...accumulatedExtraction, ...extraction };
              setLastExtraction(extraction);
              setAccumulatedExtraction(mergedExtraction);
              sessionStorage.setItem("accumulatedExtraction", JSON.stringify(mergedExtraction));
            }
          }
        } catch (chatError) {
          console.error("Chat AI error:", chatError);
          setMessages(prev => [...prev, { role: "assistant" as const, content: analysis.summary, timestamp: new Date() }]);
        }
        toast({ title: "Foto dianalisis", description: "Bahaya terdeteksi dari gambar" });
      }
    } catch (error) {
      console.error("Photo analysis error:", error);
      toast({ title: "Photo analysis failed", description: "Photo saved, but couldn't analyze it automatically", variant: "destructive" });
    } finally {
      setIsAnalyzingPhoto(false);
    }
  };

  const handleInspectionPhoto = async (file: File) => {
    setIsAnalyzingPhoto(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const fileExt = file.name.split(".").pop();
      const fileName = `inspections/${user.id}/${Date.now()}.${fileExt}`;
      const { data, error } = await supabase.storage.from("hazard-photos").upload(fileName, file);
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from("hazard-photos").getPublicUrl(data.path);
      const userPhotoMsg = { role: "user" as const, content: "📸 Foto bukti inspeksi", timestamp: new Date(), imageUrl: publicUrl };
      setMessages(prev => [...prev, userPhotoMsg]);
      setIsLoading(true);
      const { data: inspData, error: inspError } = await supabase.functions.invoke("sorin-inspection-chat", { body: { message: "[PHOTO EVIDENCE ATTACHED]", userId: authUserId, siteId: profile?.site_id || null, photoUrl: publicUrl, inspectionContext: inspectionId ? { inspectionId } : null, conversationHistory: messages.map(m => ({ role: m.role, content: m.content })) } });
      if (inspError) throw inspError;
      if (inspData?.message) {
        setMessages(prev => [...prev, { role: "assistant" as const, content: inspData.message, timestamp: new Date() }]);
      }
      if (inspData?.toolResults?.startInspection && inspData?.inspectionId) { setInspectionStarted(true); setInspectionId(inspData.inspectionId); }
      if (inspData?.toolResults?.inspectionCompleted) { setInspectionCompleted(true); toast({ title: "Inspeksi Selesai! ✓", description: "Hasil inspeksi telah disimpan." }); }
      toast({ title: "Foto diterima", description: "Bukti inspeksi dilampirkan" });
    } catch (error) {
      console.error("Inspection photo error:", error);
      toast({ title: "Gagal upload foto", description: "Coba lagi", variant: "destructive" });
    } finally {
      setIsAnalyzingPhoto(false);
      setIsLoading(false);
    }
  };

  const quickActions = [
    { label: "Lapor Bahaya", icon: Shield, mode: 'hazard' as const, emoji: '🛡️', color: 'text-blue-600' },
    { label: "Lapor Insiden", icon: null, mode: 'incident' as const, emoji: '🚨', color: 'text-red-600' },
    { label: "Cek Cara Kerja", icon: BookOpen, mode: 'knowledge' as const, emoji: '📚', color: 'text-amber-600' },
    { label: "Inspeksi", icon: ClipboardCheck, mode: 'inspection' as const, emoji: '📋', color: 'text-green-600' }
  ];

  const handleKnowledgeQuery = async (query: string) => {
    if (!query.trim() || isLoading) return;
    const userMsg = { role: "user" as const, content: query, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInputText("");
    setIsLoading(true);
    const controller = new AbortController();
    setAbortController(controller);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const { data, error } = await supabase.functions.invoke("sorin-knowledge-query", { body: { query, hazardCategory: accumulatedExtraction?.hazard_category || null, userId: authUser?.id || null } });
      if (error) throw error;
      if (controller.signal.aborted) return;
      let responseContent = data.answer || "Maaf, tidak ditemukan informasi yang relevan.";
      if (data.sources?.length > 0) responseContent += `\n\n📚 Referensi: ${data.sources.join(', ')}`;
      if (data.corrective_actions?.length > 0) {
        setSuggestedActions(data.corrective_actions);
        responseContent += "\n\n✅ Tindakan yang disarankan:";
        data.corrective_actions.forEach((action: string, idx: number) => { responseContent += `\n${idx + 1}. ${action}`; });
      }
      setMessages(prev => [...prev, { role: "assistant" as const, content: responseContent, timestamp: new Date() }]);
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error("Knowledge query error:", error);
      setMessages(prev => [...prev, { role: "assistant" as const, content: "Maaf, ada masalah saat mencari informasi. Coba lagi nanti.", timestamp: new Date() }]);
    } finally {
      setIsLoading(false);
      setAbortController(null);
    }
  };

  const handleInspectionQuery = async (query: string) => {
    if (!query.trim() || isLoading) return;
    const userMsg = { role: "user" as const, content: query, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInputText("");
    setIsLoading(true);
    const controller = new AbortController();
    setAbortController(controller);
    try {
      const inspectionContext = inspectionId ? { inspectionId, checklistId: inspectionChecklistId, pendingPhoto: pendingPhotoForFinding } : null;
      const { data, error } = await supabase.functions.invoke("sorin-inspection-chat", { body: { message: query, userId: authUserId, siteId: profile?.site_id || null, inspectionContext, conversationHistory: messages.map(m => ({ role: m.role, content: m.content })) } });
      if (error) throw error;
      if (controller.signal.aborted) return;
      let responseContent = data.message || "Maaf, ada masalah. Coba lagi.";
      if (data.toolResults?.startInspection) { setInspectionStarted(true); if (data.inspectionId) setInspectionId(data.inspectionId); if (data.checklistId) setInspectionChecklistId(data.checklistId); }
      if (data.pendingPhotoRequest) { setPendingPhotoForFinding(true); setLastFindingSeverity(data.toolResults?.createFinding?.severity || null); } else { setPendingPhotoForFinding(false); setLastFindingSeverity(null); }
      if (data.toolResults?.completeInspection || data.toolResults?.inspectionCompleted) {
        setInspectionCompleted(true);
        setPendingPhotoForFinding(false);
        toast({ title: "Inspeksi Selesai! ✓", description: "Hasil inspeksi telah disimpan." });
        sessionStorage.removeItem("sorinChatState_inspection");
        setSavedInspectionSession(null);
      }
      setMessages(prev => [...prev, { role: "assistant" as const, content: responseContent, timestamp: new Date() }]);
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error("Inspection query error:", error);
      setMessages(prev => [...prev, { role: "assistant" as const, content: "Maaf, ada masalah saat proses inspeksi. Coba lagi nanti.", timestamp: new Date() }]);
    } finally {
      setIsLoading(false);
      setAbortController(null);
    }
  };

  const handleQuickAction = (action: typeof quickActions[0]) => {
    // Handle offline mode for incident
    if (isOffline && action.mode === 'incident') {
      setShowOfflineIncidentForm(true);
      toast({
        title: "Mode Offline",
        description: "Menggunakan form manual untuk laporan insiden",
      });
      return;
    }

    if (action.mode === 'incident') {
      navigate('/report-incident');
      return;
    }

    // If offline and trying to use AI features, show offline form
    if (isOffline && (action.mode === 'hazard' || action.mode === 'inspection')) {
      if (action.mode === 'hazard') {
        setShowOfflineForm(true);
        toast({
          title: "Mode Offline",
          description: "Menggunakan form manual karena tidak ada koneksi internet",
        });
        return;
      } else {
        toast({
          title: "Tidak Tersedia Offline",
          description: "Fitur inspeksi memerlukan koneksi internet",
          variant: "destructive",
        });
        return;
      }
    }

    // If currently in a different mode, save current session first
    if (chatMode && chatMode !== action.mode) {
      handleMinimizeChat();
    }

    // Check for existing saved session of the target mode
    const storageKey = `sorinChatState_${action.mode}`;
    const savedState = sessionStorage.getItem(storageKey);

    if (savedState) {
      // Restore that mode's session
      restoreSessionFromStorage(action.mode);
      return;
    }

    // No saved session - start fresh
    startChatWithMode(action.mode);
  };

  const startChatWithMode = (mode: 'hazard' | 'knowledge' | 'inspection', initialMessage?: string) => {
    setChatMode(mode);
    setAccumulatedExtraction(null);
    setProbeQuestions([]);
    setActionSuggestions([]);
    setSuggestedActions([]);
    setShowProofUpload(false);
    setProofPhotoFile(null);
    setProofPhotoPreview(null);
    setPhotoFile(null);
    setPhotoPreview(null);
    setInspectionId(null);
    setInspectionChecklistId(null);
    setInspectionStarted(false);
    setInspectionCompleted(false);
    setPendingPhotoForFinding(false);
    setLastFindingSeverity(null);
    sessionStorage.removeItem("accumulatedExtraction");

    const greetings = {
      hazard: t("chat.initialMessage"),
      inspection: "Hai! Area kerja mana yang mau kamu inspeksi hari ini? Sebutkan lokasinya dan aku bantu pandu inspeksinya. 📋",
      knowledge: "Hai! Mau tanya tentang cara kerja atau prosedur apa? Aku bisa bantu cari di SOP, JSA, atau dokumen perusahaan lainnya. 📚"
    };

    setMessages([{ role: "assistant", content: greetings[mode], timestamp: new Date() }]);

    // If there's an initial message, route to the correct handler
    // Note: handleSendMessage reads inputText from stale closure so for hazard we pre-populate
    if (initialMessage) {
      if (mode === 'knowledge') {
        setTimeout(() => handleKnowledgeQuery(initialMessage), 50);
      } else if (mode === 'inspection') {
        setTimeout(() => handleInspectionQuery(initialMessage), 50);
      } else {
        setInputText(initialMessage); // user presses send
      }
    }
  };

  const handleSmartInput = async (text: string) => {
    try {
      // Use AI intent detection
      const { data, error } = await supabase.functions.invoke('detect-user-intent', {
        body: { message: text }
      });

      if (error) throw error;

      const intent = data?.intent || 'knowledge_query';

      if (intent === 'hazard_report') {
        startChatWithMode('hazard', text);
      } else if (intent === 'inspection') {
        startChatWithMode('inspection', text);
      } else {
        startChatWithMode('knowledge', text);
      }
    } catch (err) {
      console.error('Intent detection failed, using fallback:', err);
      // Fallback to keyword matching
      const lowerText = text.toLowerCase();
      const hazardKeywords = ['bahaya', 'berbahaya', 'lapor', 'unsafe', 'risiko', 'rusak', 'bocor', 'tumpah', 'jatuh', 'kecelakaan', 'cedera'];
      const inspectionKeywords = ['inspeksi', 'periksa', 'cek area', 'audit', 'inspect'];

      if (hazardKeywords.some(k => lowerText.includes(k))) {
        startChatWithMode('hazard', text);
      } else if (inspectionKeywords.some(k => lowerText.includes(k))) {
        startChatWithMode('inspection', text);
      } else {
        startChatWithMode('knowledge', text);
      }
    }
  };

  const handleResetConversation = () => {
    // Clear current mode's storage
    if (chatMode) {
      sessionStorage.removeItem(`sorinChatState_${chatMode}`);
      if (chatMode === 'hazard') setSavedHazardSession(null);
      else if (chatMode === 'inspection') setSavedInspectionSession(null);
      else if (chatMode === 'knowledge') setSavedKnowledgeSession(null);
    }

    setChatMode(null);
    setMessages([]);
    setAccumulatedExtraction(null);
    setPhotoFile(null);
    setPhotoPreview(null);
    setPhotoAnalysisData(null);
    setProbeQuestions([]);
    setActionSuggestions([]);
    setSuggestedActions([]);
    setShowProofUpload(false);
    setProofPhotoFile(null);
    setProofPhotoPreview(null);
    setInputText("");
    setInspectionId(null);
    setInspectionChecklistId(null);
    setInspectionStarted(false);
    setInspectionCompleted(false);
    setPendingPhotoForFinding(false);
    setLastFindingSeverity(null);
    sessionStorage.removeItem("accumulatedExtraction");
  };

  // Stop AI generation
  const handleStopGeneration = () => {
    if (abortController) {
      abortController.abort();
      setIsLoading(false);
      setAbortController(null);
      toast({ title: "Dibatalkan", description: "Permintaan AI dibatalkan" });
    }
  };

  // Minimize chat - save state and hide UI (like SafetyCopilot X button)
  const handleMinimizeChat = () => {
    if (!chatMode) return;

    // Save current state to mode-specific storage key
    const storageKey = `sorinChatState_${chatMode}`;
    const stateToSave = {
      chatMode,
      messages: messages.slice(-20).map(m => ({
        role: m.role,
        content: m.content,
        timestamp: m.timestamp.toISOString(),
        imageUrl: m.imageUrl
      })),
      accumulatedExtraction,
      inspectionId,
      inspectionChecklistId,
      inspectionStarted,
      savedAt: new Date().toISOString()
    };
    sessionStorage.setItem(storageKey, JSON.stringify(stateToSave));

    // Update appropriate state
    if (chatMode === 'hazard') setSavedHazardSession(stateToSave as any);
    else if (chatMode === 'inspection') setSavedInspectionSession(stateToSave as any);
    else if (chatMode === 'knowledge') setSavedKnowledgeSession(stateToSave as any);

    // Just hide the chat UI - don't clear state
    setChatMode(null);
  };

  // Restore a saved chat session from storage for a specific mode
  const restoreSessionFromStorage = (mode: 'hazard' | 'knowledge' | 'inspection') => {
    const storageKey = `sorinChatState_${mode}`;
    const savedState = sessionStorage.getItem(storageKey);
    if (!savedState) {
      startChatWithMode(mode);
      return;
    }

    try {
      const parsed = JSON.parse(savedState);

      // Check 24-hour expiry
      const savedAt = new Date(parsed.savedAt);
      const hoursSinceSave = (Date.now() - savedAt.getTime()) / (1000 * 60 * 60);
      if (hoursSinceSave > 24) {
        sessionStorage.removeItem(storageKey);
        if (mode === 'hazard') setSavedHazardSession(null);
        else if (mode === 'inspection') setSavedInspectionSession(null);
        else if (mode === 'knowledge') setSavedKnowledgeSession(null);
        startChatWithMode(mode);
        return;
      }

      // Restore the session
      setChatMode(parsed.chatMode);
      setMessages(parsed.messages.map((m: any) => ({
        role: m.role,
        content: m.content,
        timestamp: new Date(m.timestamp),
        imageUrl: m.imageUrl
      })));

      // Only restore accumulatedExtraction for hazard mode
      if (mode === 'hazard' && parsed.accumulatedExtraction) {
        setAccumulatedExtraction(parsed.accumulatedExtraction);
      } else if (mode !== 'hazard') {
        // Clear hazard-specific state when entering non-hazard mode
        setAccumulatedExtraction(null);
        setSuggestedActions([]);
        setShowProofUpload(false);
      }

      setInspectionId(parsed.inspectionId || null);
      setInspectionChecklistId(parsed.inspectionChecklistId || null);
      setInspectionStarted(parsed.inspectionStarted || false);
    } catch (e) {
      console.error("Failed to restore session:", e);
      sessionStorage.removeItem(storageKey);
      if (mode === 'hazard') setSavedHazardSession(null);
      else if (mode === 'inspection') setSavedInspectionSession(null);
      else if (mode === 'knowledge') setSavedKnowledgeSession(null);
      startChatWithMode(mode);
    }
  };

  const handleProofPhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProofPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => { setProofPhotoPreview(reader.result as string); };
    reader.readAsDataURL(file);
  };

  const handleCreateResolution = async (resolutionType: 'self_corrected' | 'escalated', escalatedTo?: string) => {
    if (!accumulatedExtraction || isCreatingResolution) return;
    if (resolutionType === 'self_corrected' && !proofPhotoFile) {
      if (!showProofUpload) { setShowProofUpload(true); toast({ title: "Upload Bukti Wajib", description: "Silakan upload foto bukti perbaikan untuk konfirmasi" }); }
      else { toast({ title: "Foto Bukti Diperlukan", description: "Ambil foto bukti perbaikan sebelum konfirmasi", variant: "destructive" }); }
      return;
    }
    setIsCreatingResolution(true);
    try {
      let proofPhotoUrl = null;
      if (proofPhotoFile && profileRef.current) {
        const fileName = `proofs/${profileRef.current.id}/${Date.now()}_proof.jpg`;
        const { error: uploadError } = await supabase.storage.from("hazard-photos").upload(fileName, proofPhotoFile);
        if (!uploadError) {
          const { data: { publicUrl } } = supabase.storage.from("hazard-photos").getPublicUrl(fileName);
          proofPhotoUrl = publicUrl;
        }
      }
      const createdReportId = await submitReportFromExtraction();
      if (createdReportId) {
        const { error } = await supabase.functions.invoke("create-hazard-resolution", { body: { reportId: createdReportId, resolutionType, actionTaken: resolutionType === 'self_corrected' ? suggestedActions[0] : null, actionSuggested: suggestedActions.join('; '), takenBy: profileRef.current?.id, escalatedTo: resolutionType === 'escalated' ? escalatedTo : null, notes: null, photoAfter: proofPhotoUrl } });
        if (error) throw error;
        if (resolutionType === 'self_corrected') { toast({ title: "Bagus! +20 XP Bonus 🎉", description: "Terima kasih sudah langsung memperbaiki bahaya!" }); }
        else { toast({ title: "Laporan di-eskalasi", description: `Notifikasi dikirim ke ${escalatedTo}` }); }
      }
      setShowResolutionOptions(false);
      setSuggestedActions([]);
      setProofPhotoFile(null);
      setProofPhotoPreview(null);
      setShowProofUpload(false);
    } catch (error) {
      console.error("Resolution error:", error);
      toast({ title: "Error", description: "Gagal membuat resolusi. Coba lagi.", variant: "destructive" });
    } finally {
      setIsCreatingResolution(false);
    }
  };

  // Determine if we're in chat mode
  const isInChatMode = chatMode !== null && messages.length > 0;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--gradient-page)' }}>

      {profile && <ClarificationPopup workerId={profile.id} />}
      {profile && <InterventionPopup workerId={profile.id} />}

      {/* Offline Hazard Form */}
      <AnimatePresence>
        {showOfflineForm && profile && (
          <OfflineHazardForm
            profile={{
              id: profile.id,
              site_id: profile.site_id,
              company_id: profile.company_id,
              display_name: profile.display_name,
            }}
            coworkers={cachedCoworkers || []}
            cachedLocation={cachedCheckin?.work_location}
            onClose={() => setShowOfflineForm(false)}
            onSubmitSuccess={() => {
              setShowOfflineForm(false);
              toast({
                title: "Laporan Tersimpan",
                description: "Akan dikirim otomatis saat koneksi kembali",
              });
            }}
          />
        )}
      </AnimatePresence>

      {/* Offline Incident Form */}
      <AnimatePresence>
        {showOfflineIncidentForm && profile && (
          <OfflineIncidentForm
            profile={{
              id: profile.id,
              site_id: profile.site_id,
              company_id: profile.company_id,
              display_name: profile.display_name,
            }}
            onClose={() => setShowOfflineIncidentForm(false)}
            onSubmitSuccess={() => {
              setShowOfflineIncidentForm(false);
              toast({
                title: "Insiden Tersimpan",
                description: "Akan dikirim otomatis saat koneksi kembali",
              });
            }}
          />
        )}
      </AnimatePresence>

      {/* Auto-popup for pending corroboration requests */}
      <CorroborationPopup
        isOpen={showCorroborationPopup}
        onClose={() => {
          setShowCorroborationPopup(false);
          setPendingCorroboration(null);
        }}
        reportId={pendingCorroboration?.reportId || ''}
        notificationId={pendingCorroboration?.notificationId || ''}
      />

      {/* Gemini-style Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/50 pt-safe">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isInChatMode ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleMinimizeChat}
                className="h-10 w-10 rounded-full"
                title="Tutup"
              >
                <X className="h-5 w-5" />
              </Button>
            ) : (
              <Button variant="ghost" size="icon" onClick={() => setMenuOpen(true)} className="h-10 w-10 rounded-full">
                <Menu className="h-5 w-5" />
              </Button>
            )}
            <h1 className="font-bold text-xl tracking-tight">SORIN</h1>
          </div>
          <div className="flex items-center gap-2">
            {profile && <NotificationBell workerId={profile.id} />}
            <LanguageSwitcher />
            {user && (
              <Avatar className="h-9 w-9 border-2 border-primary/20">
                <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">
                  {user.displayName.charAt(0)}
                </AvatarFallback>
              </Avatar>
            )}
          </div>
        </div>
      </header>

      {/* Slide-out Menu */}
      <AnimatePresence>
        {menuOpen && (
          <GeminiMenu
            isOpen={menuOpen}
            onClose={() => setMenuOpen(false)}
            user={user ? { displayName: user.displayName, team: user.team, xp: user.xp, streak: user.streak, role: profile?.role } : null}
          />
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-1 flex flex-col max-w-2xl w-full mx-auto">
        {/* Welcome Section - Only show when no chat mode */}
        {!isInChatMode && (
          <div className="flex-1 flex flex-col px-4 pt-8 pb-32">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-left mb-8"
            >
              {/* Personalized greeting */}
              <p className="text-muted-foreground text-xl mb-1">
                Halo, {user?.displayName.split(' ')[0] || 'Pekerja'}
              </p>

              {/* Daily Message or Default */}
              {profile && (
                <DailyMessageBanner workerId={profile.id}>
                  <h1 className="text-2xl md:text-3xl font-semibold leading-tight text-foreground">
                    Apa yang bisa saya bantu hari ini?
                  </h1>
                </DailyMessageBanner>
              )}
            </motion.div>

            {/* Continue Session Indicators for each mode */}
            {(savedHazardSession || savedInspectionSession || savedKnowledgeSession) && !isInChatMode && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 flex flex-wrap gap-2"
              >
                {savedHazardSession && (
                  <button
                    onClick={() => restoreSessionFromStorage('hazard')}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary/10 border border-primary/20 shadow-sm hover:bg-primary/20 transition-all"
                  >
                    <MessageCircle className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium text-primary">Lanjutkan Lapor Bahaya</span>
                  </button>
                )}
                {savedInspectionSession && (
                  <button
                    onClick={() => restoreSessionFromStorage('inspection')}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary/10 border border-primary/20 shadow-sm hover:bg-primary/20 transition-all"
                  >
                    <MessageCircle className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium text-primary">Lanjutkan Inspeksi</span>
                  </button>
                )}
                {savedKnowledgeSession && (
                  <button
                    onClick={() => restoreSessionFromStorage('knowledge')}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary/10 border border-primary/20 shadow-sm hover:bg-primary/20 transition-all"
                  >
                    <MessageCircle className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium text-primary">Lanjutkan Cek Cara Kerja</span>
                  </button>
                )}
              </motion.div>
            )}

            {/* Gemini-style Action Chips - Inline pills that fit text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex flex-col items-start gap-2.5"
            >
              {quickActions.map((action, idx) => {
                const isIncident = action.mode === 'incident';
                // Use module-level vars so timers survive React re-renders during long press
                const handleTouchStart = () => {
                  if (!isIncident) return;
                  _didLongPress = false;
                  _longPressTimer = setTimeout(() => {
                    _didLongPress = true;
                    navigator.vibrate?.(50);
                    navigate('/quick-incident');
                  }, 600);
                };

                const handleTouchEnd = () => {
                  if (_longPressTimer) {
                    clearTimeout(_longPressTimer);
                    _longPressTimer = null;
                  }
                };

                const handleClick = () => {
                  if (isIncident && _didLongPress) return;
                  handleQuickAction(action);
                };

                return (
                  <button
                    key={action.mode}
                    onClick={handleClick}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    onTouchCancel={handleTouchEnd}
                    onMouseDown={isIncident ? handleTouchStart : undefined}
                    onMouseUp={isIncident ? handleTouchEnd : undefined}
                    onMouseLeave={isIncident ? handleTouchEnd : undefined}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-card border border-border/60 shadow-[var(--shadow-card)] hover:shadow-[var(--shadow-soft)] hover:border-border transition-all"
                  >
                    <span className="text-lg">{action.emoji}</span>
                    <span className="font-medium text-foreground text-sm">{action.label}</span>
                  </button>
                );
              })}
            </motion.div>

            {/* Quest Gift Box */}
            {profile && <QuestGiftBox workerId={profile.id} />}

            {/* Floating Input Bar - Gemini style "Ask SORIN" */}
            <div className="fixed bottom-4 left-4 right-4 max-w-xl mx-auto z-30">
              <div className="bg-secondary rounded-2xl border border-border shadow-[var(--shadow-soft)]">
                <input
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && inputText.trim()) {
                      e.preventDefault();
                      handleSmartInput(inputText);
                    }
                  }}
                  placeholder="Tanya SORIN..."
                  className="w-full border-0 bg-transparent px-4 pt-4 pb-2 text-base focus:outline-none h-auto"
                />
                <div className="flex items-center justify-between px-3 pb-3">
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 rounded-full"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Plus className="h-5 w-5 text-muted-foreground" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-1">
                    {inputText.trim() ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 rounded-full bg-primary/10"
                        onClick={() => handleSmartInput(inputText)}
                      >
                        <Send className="h-5 w-5 text-primary" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 rounded-full"
                        onClick={handleMicClick}
                      >
                        <Mic className={`h-5 w-5 ${isRecording ? 'text-destructive animate-pulse' : 'text-muted-foreground'}`} />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Chat Messages */}
        {isInChatMode && (
          <>
            {/* Mode indicator with Reset button */}
            <div className="px-4 pt-4 flex items-center justify-between">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
                {chatMode === 'hazard' && <Shield className="h-4 w-4 text-primary" />}
                {chatMode === 'knowledge' && <BookOpen className="h-4 w-4 text-primary" />}
                {chatMode === 'inspection' && <ClipboardCheck className="h-4 w-4 text-primary" />}
                <span className="text-sm font-medium text-primary">
                  {chatMode === 'hazard' ? 'Lapor Bahaya' : chatMode === 'knowledge' ? 'Cek Cara Kerja' : 'Inspeksi'}
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleResetConversation}
                className="h-8 w-8 rounded-full"
                title="Reset percakapan"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex-1 px-4 py-6 space-y-4 overflow-y-auto">
              {messages.map((msg, idx) => {
                // Always show retry for assistant messages
                const isAssistant = msg.role === 'assistant';

                const handleRetry = () => {
                  // Find the last user message before this AI message
                  const previousMessages = messages.slice(0, idx);
                  let lastUserMsgIdx = -1;
                  for (let i = previousMessages.length - 1; i >= 0; i--) {
                    if (previousMessages[i].role === 'user') {
                      lastUserMsgIdx = i;
                      break;
                    }
                  }

                  if (lastUserMsgIdx >= 0) {
                    const lastUserMsg = previousMessages[lastUserMsgIdx];
                    // Remove this AI message from state
                    setMessages(prev => prev.filter((_, i) => i !== idx));
                    // Directly retry with content — no state race condition
                    retryWithContent(lastUserMsg.content, chatMode || 'hazard');
                  }
                };

                const handleEditMessage = (content: string) => {
                  // Remove this message and all messages after it
                  setMessages(prev => prev.slice(0, idx));
                  // Set input to the content for user to edit
                  setInputText(content);
                };

                return (
                  <ChatBubble
                    key={`${msg.role}_${msg.timestamp.getTime()}`}
                    role={msg.role}
                    content={msg.content}
                    timestamp={msg.timestamp}
                    imageUrl={msg.imageUrl}
                    showRetry={isAssistant}
                    onRetry={isAssistant ? handleRetry : undefined}
                    onEdit={msg.role === 'user' ? handleEditMessage : undefined}
                  />
                );
              })}
              {isLoading && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                    <Loader2 className="h-4 w-4 animate-spin text-primary-foreground" />
                  </div>
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          </>
        )}

        {/* Unified Bottom Input Area - Only show when in chat mode */}
        {isInChatMode && (
          <div className="sticky bottom-0 bg-background/95 backdrop-blur-xl border-t border-border/50 p-4 pb-safe z-20">
            <SubscriptionGatedInputBar
              fileInputRef={fileInputRef}
              inputText={inputText}
              setInputText={setInputText}
              handleSendMessage={handleSendMessage}
              handleCameraClick={handleCameraClick}
              handleMicClick={handleMicClick}
              handlePhotoSelect={handlePhotoSelect}
              isLoading={isLoading}
              isAnalyzingPhoto={isAnalyzingPhoto}
              isRecording={isRecording}
              onStop={handleStopGeneration}
              companyId={profile?.company_id || null}
              aboveInputContent={
                <>
                  {/* Photo Preview */}
                  {photoPreview && (
                    <div className="mb-3 relative inline-block">
                      <img src={photoPreview} alt="Preview" className="h-20 w-20 object-cover rounded-2xl border-2 border-border" />
                      <button onClick={() => { setPhotoFile(null); setPhotoPreview(null); }} className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full h-6 w-6 flex items-center justify-center text-xs">✕</button>
                    </div>
                  )}

                  {/* Resolution Options - ONLY in hazard mode */}
                  {chatMode === 'hazard' && accumulatedExtraction && accumulatedExtraction.hazard_classification && accumulatedExtraction.hazard_category && messages.length > 2 && (
                    <div className="mb-3 p-4 rounded-2xl bg-secondary border border-border shadow-sm space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-foreground">
                          {accumulatedExtraction.reported_person
                            ? `${accumulatedExtraction.reported_person} - ${accumulatedExtraction.hazard_category}`
                            : accumulatedExtraction.hazard_category?.replace(/_/g, ' ')
                          }
                        </p>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${accumulatedExtraction.risk_matrix?.risk_rating === 'high' || accumulatedExtraction.risk_matrix?.risk_rating === 'very_high' ? 'bg-destructive/20 text-destructive' : 'bg-muted text-muted-foreground'}`}>
                          {accumulatedExtraction.risk_matrix?.risk_rating || 'low'}
                        </span>
                      </div>

                      {(suggestedActions.length > 0 || accumulatedExtraction.resolution_options?.primary_action) && (
                        <div className="text-sm bg-background/60 p-3 rounded-xl">
                          <p className="font-medium text-foreground mb-2 flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-primary" />
                            Saran tindakan:
                          </p>
                          <ul className="space-y-1.5 text-muted-foreground">
                            {suggestedActions.slice(0, 3).map((action, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="text-primary mt-0.5">•</span>
                                <span>{action}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {showProofUpload && (
                        <div className="bg-background/60 p-3 rounded-xl space-y-2">
                          <p className="text-sm font-medium flex items-center gap-2">
                            <Camera className="h-4 w-4 text-primary" />
                            Upload foto bukti perbaikan:
                          </p>
                          <input
                            ref={proofInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleProofPhotoSelect}
                            style={{ position: 'absolute', width: '1px', height: '1px', opacity: 0, overflow: 'hidden', pointerEvents: 'none' }}
                          />
                          {proofPhotoPreview ? (
                            <div className="relative inline-block">
                              <img src={proofPhotoPreview} alt="Bukti" className="h-24 w-24 object-cover rounded-xl border-2 border-success" />
                              <button onClick={() => { setProofPhotoFile(null); setProofPhotoPreview(null); }} className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full h-6 w-6 flex items-center justify-center text-xs">✕</button>
                            </div>
                          ) : (
                            <Button onClick={() => proofInputRef.current?.click()} variant="outline" size="sm" className="w-full">
                              <Camera className="h-4 w-4 mr-2" />
                              Ambil Foto Bukti
                            </Button>
                          )}
                          <div className="flex gap-2 pt-2">
                            <Button onClick={() => handleCreateResolution('self_corrected')} disabled={isCreatingResolution} size="sm" className="flex-1 bg-success hover:bg-success/90">
                              {isCreatingResolution ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                              Konfirmasi Selesai
                            </Button>
                            <Button onClick={() => setShowProofUpload(false)} variant="ghost" size="sm">Batal</Button>
                          </div>
                        </div>
                      )}

                      {!showProofUpload && (
                        <>
                          <div className="pt-2 border-t border-border/50">
                            <p className="text-sm font-medium mb-2">Bisa kamu perbaiki sendiri?</p>
                            <div className="flex flex-col gap-2">
                              <Button onClick={() => { if (accumulatedExtraction.resolution_options?.requires_proof_photo) { setShowProofUpload(true); } else { handleCreateResolution('self_corrected'); } }} disabled={isCreatingResolution} size="sm" className="w-full bg-success hover:bg-success/90">
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Sudah Diperbaiki
                              </Button>
                              <Button onClick={() => handleCreateResolution('escalated', 'supervisor')} disabled={isCreatingResolution} size="sm" variant="outline" className="w-full">
                                <AlertTriangle className="h-4 w-4 mr-2" />
                                Butuh Bantuan
                              </Button>
                            </div>
                          </div>
                          <Button onClick={submitReportFromExtraction} disabled={isSavingReport} variant="ghost" className="w-full text-muted-foreground text-sm" size="sm">
                            {isSavingReport ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Menyimpan...</> : <><Upload className="h-4 w-4 mr-2" />Simpan Saja</>}
                          </Button>
                        </>
                      )}
                    </div>
                  )}

                  {/* Inspection Completed */}
                  {chatMode === 'inspection' && inspectionCompleted && (
                    <div className="w-full mb-3 p-3 rounded-2xl bg-success/10 border border-success/20 text-center">
                      <CheckCircle className="h-5 w-5 inline mr-2 text-success" />
                      <span className="text-success font-medium">Inspeksi Telah Disimpan</span>
                    </div>
                  )}

                  {/* Inspection Quick Response Chips */}
                  {chatMode === 'inspection' && inspectionStarted && !inspectionCompleted && (
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {pendingPhotoForFinding ? (
                        <>
                          <button onClick={() => fileInputRef.current?.click()} disabled={isLoading || isAnalyzingPhoto} className="col-span-2 px-4 py-3 min-h-[48px] rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium transition-colors flex items-center justify-center gap-2">
                            📸 Kirim Foto Bukti
                          </button>
                          <button onClick={() => { setPendingPhotoForFinding(false); handleInspectionQuery("Lanjut tanpa foto"); }} disabled={isLoading} className="col-span-2 px-4 py-3 min-h-[44px] rounded-full bg-muted hover:bg-muted/80 border border-border text-muted-foreground text-sm font-medium transition-colors flex items-center justify-center">
                            Lanjut Tanpa Foto
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => handleInspectionQuery("Kondisi sesuai, tidak ada masalah")} disabled={isLoading} className="px-3 py-3 min-h-[44px] rounded-full bg-success/10 hover:bg-success/20 border border-success/30 text-success text-sm font-medium transition-colors flex items-center justify-center gap-1">
                            ✓ Sesuai
                          </button>
                          <button onClick={() => handleInspectionQuery("Ada masalah yang perlu dicatat")} disabled={isLoading} className="px-3 py-3 min-h-[44px] rounded-full bg-destructive/10 hover:bg-destructive/20 border border-destructive/30 text-destructive text-sm font-medium transition-colors flex items-center justify-center gap-1">
                            ✗ Masalah
                          </button>
                          <button onClick={() => fileInputRef.current?.click()} disabled={isLoading || isAnalyzingPhoto} className="px-3 py-3 min-h-[44px] rounded-full bg-primary/10 hover:bg-primary/20 border border-primary/30 text-primary text-sm font-medium transition-colors flex items-center justify-center gap-1">
                            📸 Foto
                          </button>
                          <button onClick={() => handleInspectionQuery("Sudah selesai, tolong buat ringkasan")} disabled={isLoading} className="px-3 py-3 min-h-[44px] rounded-full bg-muted hover:bg-muted/80 border border-border text-muted-foreground text-sm font-medium transition-colors flex items-center justify-center">
                            Selesai
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </>
              }
            />
          </div>
        )}


      </main>
    </div>
  );
};

export default Home;
